import { useSignal } from '@preact/signals';
import { round } from 'es-toolkit';
import { useEffect, useMemo } from 'preact/hooks';
import waitForTimers from 'wait-for-timers';

export function roundIfClose(seconds: number, threshold = 0.05) {
  const fractionalPart = seconds % 1;
  return fractionalPart >= 1 - threshold ? Math.ceil(seconds) : Math.floor(seconds);
}

// Round seek down for display in UI, unless very close to the next number
const threshold = 0.05;
const roundSeek = (val: number) => roundIfClose(val, threshold);

// Accepts an arbitrary float for seek and returns a rounded integer. Will increment
// seek automatically if playing and hasn't gotten an update for a while.
export function useSmoothSeek(
  lastSeek: number,
  isPlaying: boolean,
  playbackRate: number
) {
  const seekSetAt = useMemo(() => Date.now(), [lastSeek, isPlaying]); // When was seek last updated?
  const displayedSeek = useSignal(roundSeek(lastSeek));
  const maxIncrement = round(3 * playbackRate); // Never simulate seek updates more than a few seconds ahead

  useEffect(() => {
    // Happy path: just round the seek and return the rounded value immediately
    displayedSeek.value = roundSeek(lastSeek);

    if (!isPlaying) return;

    // While playing, perodically use a setTimeout to increment the displayed seek if we haven't
    // gotten an update for a while
    let incrementCount = 1;
    let cancelTimer: () => void;
    const tick = () => {
      displayedSeek.value++;
      incrementCount++;
      // console.log('-- tick', displayedSeek.value);
      if (incrementCount >= maxIncrement) {
        console.warn(
          `Incremented seek ${incrementCount} times while waiting for update`
        );
        return;
      }
      cancelTimer = waitForTimers([getTimeToNextDisplayedSeek()], tick);
    };
    cancelTimer = waitForTimers([getTimeToNextDisplayedSeek()], tick);

    return () => {
      // console.log('-- X Canceling timer');
      cancelTimer();
    };

    function getTimeToNextDisplayedSeek() {
      const nextDisplayedSeek = displayedSeek.peek() + 1;
      const driftSeconds = (Date.now() - seekSetAt) / 1000;

      // Wait an extra 50ms to ensure that the native wrapper or whatever is actually setting the seek
      // value is late on the beat and the current value is actually stale. We don't want to be in the
      // business of setting a predicted seek value a couple ms before the actual seek value gets set,
      // as this just causes an extra re-render for now good reason. Better to be a tad late than too hasty.
      const extraWait = 0.05;

      const time =
        ((nextDisplayedSeek - lastSeek - driftSeconds + extraWait) * 1000) /
        playbackRate;
      // console.log('-- Time to next displayed seek', time, {
      //   seekSetAt,
      //   lastSeek,
      //   laskSeekSetAgo: driftSeconds,
      //   nextDisplayedSeek,
      //   displayedSeek: displayedSeek.peek(),
      // });
      return time;
    }
  }, [lastSeek, isPlaying, playbackRate, maxIncrement]);

  return displayedSeek;
}
